import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios'
import { Grid, Header, Label, Message, Segment } from 'semantic-ui-react'
import { toast } from 'react-toastify'
import React, { useMemo, useRef } from 'react'
import { Form as FinalForm } from 'react-final-form'
import { Link, Route, Switch, useHistory, useRouteMatch } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'

import { Alias, ProductForm as ProductFormType } from '../../redux/types/products'
import { getInitialProductFormValues } from '../../redux/reducers/ui'
import { ProductForm } from './ProductForm'
import { API_PRODUCTS, PATH_BATCH_LOOKUP, PATH_ROOT } from '../../consts'
import { mutators } from '../../utils/mutators'
import { to } from '../../utils/to'
import { sleep } from '../../utils/sleep'
import { ProductFlags } from './ProductFlags'
import { ProductAvailabilityInfo } from './ProductAvailabilityInfo'
import { LookupBar } from './LookupBar'
import { EditActions } from './ProductForm/EditActions'
import { handleGenericErrors, handleValidationErrors } from '../../utils/Products/errorHandler'
import { CreateLocalizationPage } from '../../pages/CreateLocalizationPage'
import { EditLocalizationPage } from '../../pages/EditLocalizationPage'
import { validate } from './Form/validate'
import { ImageList } from '@iheartjane/react-image-list'
import { useSelector } from 'react-redux'
import { AppState } from '../../redux/store'
import { toProductUpdateApiFormat } from './utils/formatPayloads'
import { sortBy } from 'lodash'
import { Duplicates } from '../../utils/aliases/Duplicates'

export const EditProduct = ({
  product,
  loading,
  refetchProduct,
}: {
  product?: any
  loading: boolean
  refetchProduct: () => void
}) => {
  const productFormPristine = useRef(true)
  const { path } = useRouteMatch()
  const { currentBatchId } = useSelector((state: AppState) => state.ui)
  const history = useHistory()

  /**
   * Takes a product returned from the products api and transforms
   * it to suit the ProductForm component.
   */
  const toProductFormFormat = (product: any): ProductFormType | { image_urls: string[] } => {
    if (!product) {
      return { image_urls: [] }
    }
    const initialProductValues = getInitialProductFormValues()
    Object.keys(initialProductValues).forEach((key: string) => {
      if (product[key]) {
        initialProductValues[key] = product[key]
      }
    })

    // Transform array fields into a format expected by the server (See Rails Pattern: accept_nested_attributes_for)
    const { aliases } = product

    const duplicates = new Duplicates(aliases)

    const sortedAliases = sortBy(aliases, (alias: Alias) => [
      -duplicates.isPaddedDuplicate(alias),
      alias.name,
    ])

    // mark duplicate aliases to be deleted
    initialProductValues['aliases_attributes'] = sortedAliases
      ? sortedAliases.map((alias: Alias) => ({
          id: alias.id,
          name: alias.name,
          _destroy: duplicates.isPaddedDuplicate(alias),
        }))
      : []

    return initialProductValues
  }

  const initialProductFormValues = useMemo(() => {
    return toProductFormFormat(product)
  }, [JSON.stringify(product)]) // eslint-disable-line

  const onSubmit = async (values: ProductFormType) => {
    const payload = toProductUpdateApiFormat(values)

    const axiosRequestObj: AxiosRequestConfig = {
      url: `${API_PRODUCTS}/${values.id}`,
      method: 'PATCH',
      data: payload,
      headers: { 'content-type': 'application/json' },
      withCredentials: true,
    }

    const [error, response] = await to<AxiosResponse, AxiosError>(axios(axiosRequestObj))

    if (response) {
      // Sleep to allow the monolith worker enough time to perform updates before refetching the product
      await sleep(500)
      toast('Product updated', { containerId: 'default' })
      await refetchProduct()
      history.push(history.location.pathname)
    } else if (error) {
      const validations = error.response?.data?.errors?.validations

      if (validations) {
        handleValidationErrors(validations)
      } else {
        handleGenericErrors(error)
      }
    }

    if (currentBatchId) {
      history.push(PATH_BATCH_LOOKUP)
    }
  }

  return (
    <>
      <Segment basic padded style={{ margin: 0 }} attached="bottom">
        <Header as="h2">
          {product ? (
            <>
              Edit product "{product?.name}"
              <Label
                as="a"
                color="grey"
                onClick={(e) => {
                  e.preventDefault()
                  navigator.clipboard.writeText(`${product?.id}`)
                }}
              >
                ID #{product?.id}
              </Label>
            </>
          ) : (
            'Edit product'
          )}
        </Header>
      </Segment>

      <LookupBar
        productId={product?.id}
        loading={loading}
        productFormPristine={productFormPristine}
        showCreateNoticeButton={!product?.notice_status}
      />

      {product ? (
        <>
          <Segment basic padded style={{ paddingTop: 0, paddingBottom: 0, margin: 0 }}>
            <ProductFlags product={product} refetchProduct={refetchProduct} />
            <ProductAvailabilityInfo product={product} />
          </Segment>

          <Segment basic padded>
            <Grid stackable>
              <Grid.Row>
                <Grid.Column width={16}>
                  <FinalForm
                    onSubmit={onSubmit}
                    validate={validate}
                    initialValues={initialProductFormValues}
                    mutators={mutators}
                  >
                    {(finalFormProps) => (
                      <ImageList initialImages={initialProductFormValues.image_urls}>
                        <ProductForm
                          productFormPristine={productFormPristine}
                          actions={EditActions}
                          enableAliasRemap={true}
                          {...finalFormProps}
                        />
                      </ImageList>
                    )}
                  </FinalForm>
                </Grid.Column>
              </Grid.Row>

              <Grid.Row>
                <Grid.Column width={10}>
                  <Switch>
                    <Route path={`${path}/localizations/new`}>
                      <Helmet>
                        <title>{`Edit Product #${product.id}`}</title>
                      </Helmet>
                      <CreateLocalizationPage />
                    </Route>

                    <Route path={`${path}/localizations/:localizationId`}>
                      <EditLocalizationPage />
                    </Route>

                    <Route path={`${path}`}>
                      <Helmet>
                        <title>Edit Product</title>
                      </Helmet>
                      <EditLocalizationPage />
                    </Route>
                  </Switch>
                </Grid.Column>
              </Grid.Row>
            </Grid>
          </Segment>
        </>
      ) : (
        <Segment basic padded style={{ paddingTop: 0, paddingBottom: 0, margin: 0 }}>
          <Helmet>
            <title>Edit Product</title>
          </Helmet>
          <Message>
            Enter a product id or select a product from <Link to={PATH_ROOT}>Lookup</Link>.
          </Message>
        </Segment>
      )}
    </>
  )
}
